import { forwardRef, useState } from "react";

// mui
import { TextField, useTheme, IconButton, InputAdornment } from "@mui/material";

// imask
import { IMaskInput } from "react-imask"; 

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";


const PassportMask = forwardRef(function PhoneMask(props, ref) {
  const { onChange, ...other } = props;      

  return (
    <IMaskInput
      {...other}
      mask="0000 000000"                 
      inputRef={ref}                            
      overwrite
      onAccept={(value) =>
        onChange?.({ target: { name: props.name, value } }) 
      }
    />
  );
});

const SnilsMask = forwardRef(function PhoneMask(props, ref) {
  const { onChange, ...other } = props;      

  return (
    <IMaskInput
      {...other}
      mask="000-000-000 00"                 
      inputRef={ref}                            
      overwrite
      onAccept={(value) =>
        onChange?.({ target: { name: props.name, value } }) 
      }
    />
  );
});

const PolicyMask = forwardRef(function PhoneMask(props, ref) {
  const { onChange, ...other } = props;      

  return (
    <IMaskInput
      {...other}
      mask="0000000000000000"                 
      inputRef={ref}                            
      overwrite
      onAccept={(value) =>
        onChange?.({ target: { name: props.name, value } }) 
      }
    />
  );
});

const DMSMask = forwardRef(function DMSMask(props, ref) {
  const { onChange, ...other } = props;      

  return (
    <IMaskInput
      {...other}
      mask="000-000-00000" // Формат: 123-456-88899                
      inputRef={ref}                            
      overwrite
      onAccept={(value) =>
        onChange?.({ target: { name: props.name, value } }) 
      }
    />
  );
});

const LettersMask = forwardRef(function LettersMask(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask={/^[A-Za-zА-Яа-яЁё\s-]*$/}
      inputRef={ref}
      overwrite={false}
      onAccept={(value) =>
        onChange?.({ target: { name: props.name, value } })
      }
    />
  );
});

// Заглавная буква в начале слова и после дефиса («Петров-Водкин»); после пробела не трогаем - «оглы» пишется со строчной
const capitalizeNameChar = (ch, masked) =>
  masked.value === '' || masked.value.endsWith('-') ? ch.toUpperCase() : ch;

// Фамилия / имя: одно слово из русских или латинских букв, дефис только между буквами, без пробелов
const PersonNameMask = forwardRef(function PersonNameMask(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask={/^([A-Za-zА-Яа-яЁё]+(-[A-Za-zА-Яа-яЁё]+)*-?)?$/}
      // вставка «Иванов Иван Иванович» целиком - оставляем только первое слово
      prepare={(str) => str.trim().split(/\s+/)[0]}
      prepareChar={capitalizeNameChar}
      inputRef={ref}
      overwrite={false}
      onAccept={(value) =>
        onChange?.({ target: { name: props.name, value } })
      }
    />
  );
});

// Отчество: как имя, но допускается одиночный пробел (для «оглы», «кызы»)
const PatronymicMask = forwardRef(function PatronymicMask(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask={/^([A-Za-zА-Яа-яЁё]+([- ][A-Za-zА-Яа-яЁё]+)*[- ]?)?$/}
      prepareChar={capitalizeNameChar}
      inputRef={ref}
      overwrite={false}
      onAccept={(value) =>
        onChange?.({ target: { name: props.name, value } })
      }
    />
  );
});

const NumberMask = forwardRef(function NumberMask(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask={/^[0-9]*$/} // Только цифры
      inputRef={ref}
      overwrite={false}
      onAccept={(value) =>
        onChange?.({ target: { name: props.name, value } })
      }
    />
  );
});

// Для мобильного телефона
const MobilePhoneMask = forwardRef(function MobilePhoneMask(props, ref) {
  const { onChange, ...other } = props;      

  return (
    <IMaskInput
      {...other}
      mask="+7 (000) 000-00-00"
      // вставка целого номера: 89161234567 / +7 916 123-45-67 / 79161234567 → 9161234567
      prepare={(str, masked) => {
        const digits = str.replace(/\D/g, '');
        if (!masked.unmaskedValue && digits.length === 11 && /^[78]/.test(digits)) {
          return digits.slice(1);
        }
        return str;
      }}
      // ручной ввод: первая набранная «8» отбрасывается (префикс +7 уже стоит)
      prepareChar={(ch, masked) =>
        ch === '8' && !masked.unmaskedValue ? '' : ch
      }
      inputRef={ref}
      overwrite
      onAccept={(value) => {
        onChange?.({ target: { name: props.name, value } });
      }}
    />
  );
});

// Для домашнего телефона
const HomePhoneMask = forwardRef(function HomePhoneMask(props, ref) {
  const { onChange, ...other } = props;      

  return (
    <IMaskInput
      {...other}
      mask="0-00-00" // Гибкая маска
      definitions={{
        '0': /[0-9]/
      }}
      inputRef={ref}                            
      overwrite
      onAccept={(value) => {
        onChange?.({ target: { name: props.name, value } });
      }}
    />
  );
});

const TextWithPunctuationMask = forwardRef(function TextWithPunctuationMask(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      // Разрешаем: буквы (англ/рус), цифры, пробелы, дефисы, точки, запятые
      mask={/^[A-Za-zА-Яа-яЁё0-9\s@\-\.,!?;:"'()]*$/}
      inputRef={ref}
      overwrite={false}
      // Подготавливаем значение (опционально)
      prepare={(value) => {
        // Можно добавить логику подготовки, если нужно
        return value;
      }}
      onAccept={(value) =>
        onChange?.({ target: { name: props.name, value } })
      }
    />
  );
});

const OldPasswordMask = forwardRef(function OldPasswordMask(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask={/^[A-Za-zА-Яа-яЁё0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]*$/}
      inputRef={ref}
      overwrite={false}
      onAccept={(value) => {
        onChange?.({ target: { name: props.name, value } });
      }}
    />
  );
});

const NewPasswordMask = forwardRef(function NewPasswordMask(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask={/^[A-Za-zА-Яа-яЁё0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]*$/}
      definitions={{
        'U': /[A-Z]/,
        'L': /[a-z]/,
        'D': /[0-9]/,
        'S': /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/
      }}
      inputRef={ref}
      overwrite={false}
      onAccept={(value) => {
        onChange?.({ target: { name: props.name, value } });
      }}
    />
  );
});

const ConfirmPasswordMask = forwardRef(function ConfirmPasswordMask(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask={/^[A-Za-zА-Яа-яЁё0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]*$/}
      inputRef={ref}
      overwrite={false}
      onAccept={(value) => {
        onChange?.({ target: { name: props.name, value } });
      }}
    />
  );
});

const MASK = {
  passport: PassportMask,
  snils: SnilsMask,
  policy: PolicyMask,
  dms: DMSMask,
  letter: LettersMask,
  personName: PersonNameMask,
  patronymic: PatronymicMask,
  number: NumberMask,
  phone: MobilePhoneMask,
  homePhone: HomePhoneMask,
  mixText: TextWithPunctuationMask,
  oldPassword: OldPasswordMask,     // Для старого пароля
  newPassword: NewPasswordMask,     // Для нового пароля с валидацией
  confirmPassword: ConfirmPasswordMask, // Для подтверждения пароля
}

export default function ConfirmInput({ 
  mask = '', 
  label, 
  value, 
  onChange, 
  slotProps={}, 
  name, 
  readOnly = false, 
  disabled = false, 
  type='text', 
  sx = {},
  required = false,
  error = false,
  helperText,
  redBorder = false,
}) {
  const theme = useTheme();
  const maskComponent = mask ? MASK[mask] : undefined;

  // Состояние для отображения пароля
  const [showPassword, setShowPassword] = useState(false);

  // Обработчик клика по иконке
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Определяем, является ли поле паролем
  const isPasswordField = type === 'password' || 
    mask === 'oldPassword' || 
    mask === 'newPassword' || 
    mask === 'confirmPassword';
  
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const inputProps = {
    ...(maskComponent && { inputComponent: maskComponent }),
    readOnly: readOnly,
    style: readOnly ? { cursor: 'default' } : {},
    type: isPasswordField && !showPassword ? 'password' : 'text',
    
    // Добавляем иконку для пароля
    endAdornment: isPasswordField && !readOnly ? (
      <InputAdornment position="end">
        <IconButton
          aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
          onClick={handleClickShowPassword}
          onMouseDown={handleMouseDownPassword}
          edge="end"
          sx={{ 
            color: 'rgd.text2',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ) : null,
    
    // Фильтруем кастомные пропсы
    ...Object.keys(slotProps.input || {}).reduce((acc, key) => {
      if (key.startsWith('on') || key === 'ref') {
        acc[key] = slotProps.input[key];
      }
      return acc;
    }, {})
  };

  return (
    <TextField
      name={name}
      label={label}
      value={value}
      required={required}
      error={error}
      helperText={helperText}
      onChange={readOnly ? undefined : onChange} // отключаем onChange в режиме readOnly
      disabled={disabled}
      slotProps={{
        ...slotProps,
        input: inputProps
      }}
      autoComplete="off"
      sx={{
        ...sx,
        width: '100%',
        '& .MuiInputLabel-root': {
          color: 'rgd.text2',
          fontWeight: 600,
          top: '50%',
          left: 12,
          transform: 'translate(0, -50%) scale(1)',
          transformOrigin: 'top left',
          transition: 'all 0.1s ease-out',
          '&.Mui-focused': {
            color: 'rgd.text2'
          }
        },
        '& .MuiInputLabel-shrink': {
          top: 0,
          transform: 'translate(14px, -6px) scale(0.75)',
          color: 'rgd.text2', 
          backgroundColor: 'transparent',
          left: 0,
        },
        '& .MuiOutlinedInput-root': {
          height: 48,
          // border: `2px solid ${theme.palette.rgd.backgroundComponent}`,
          backgroundColor: 'rgd.backgroundComponent',

          borderRadius: '0.75rem',
          '& fieldset': {
            borderColor: redBorder ? 'red' : 'rgd.inputBorder',
            borderWidth: 1,
          },
          
          '&:hover:not(.Mui-disabled):not(.Mui-readOnly) fieldset': {
            borderColor: 'rgd.primary', 
            borderWidth: 2,
            transition: 'border 0.1s ease' 
          },
          ...(readOnly && {'&:hover fieldset': {
              borderColor: 'rgd.inputBorder',
            },
          }),
          // ФОКУС (focus) - вот здесь меняется border при клике/фокусе
          '&.Mui-focused:not(.Mui-disabled) fieldset': {
            borderColor: 'rgd.primary', 
            borderWidth: 2,
            transition: 'border 0.05s ease' 
          },
          '&.Mui-focused': {
            color: 'inherit',
            boxShadow: 'none',
          },
          '&.Mui-error fieldset, &.Mui-error:hover:not(.Mui-disabled):not(.Mui-readOnly) fieldset, &.Mui-error.Mui-focused fieldset': {
            borderColor: 'error.main',
          },
          ...(readOnly && {'&.Mui-focused fieldset': {
              borderColor: 'rgd.inputBorder',
            },
          }),
          '& input': {
            fontSize: {xs: 16, sm: 18},
            fontWeight: '400',
            ...(readOnly && {
                cursor: 'default',
                caretColor: 'transparent',
            }),
          },
           // Отключаем курсор мыши на всем поле в режиме чтения
            ...(readOnly && {
              cursor: 'default',
          }),
        },
        // Убираем возможность фокуса в режиме чтения
        ...(readOnly && {
            '& .MuiInputBase-root': {
                pointerEvents: 'none',
            },
        }),
        '& .MuiInputLabel-asterisk': {
          color: 'rgd.primary'
        },
      }}
    >

    </TextField>
  )
}